import { Gender } from "../pages/OrderForms/orders";
import type { IdType } from "@ifa/utils";

/**
 * 身份证解析结果
 */
export interface IdCardParseResult {
  /** 出生日期 (YYYY-MM-DD) */
  birthDate?: string;
  /** 性别 */
  gender?: Gender;
  /** 是否解析成功 */
  success: boolean;
}

/**
 * 从中国大陆身份证号码中提取出生日期和性别
 *
 * 身份证号码格式：
 * - 18位：前6位地区码 + 8位出生日期(YYYYMMDD) + 3位顺序码 + 1位校验码
 * - 15位（旧版）：前6位地区码 + 6位出生日期(YYMMDD) + 3位顺序码
 *
 * 性别判断：倒数第2位（顺序码最后一位）奇数为男，偶数为女
 */
function parseChinaIdCard(idNumber: string): IdCardParseResult {
  const trimmed = idNumber.trim();

  // 18位身份证
  if (trimmed.length === 18) {
    const year = trimmed.substring(6, 10);
    const month = trimmed.substring(10, 12);
    const day = trimmed.substring(12, 14);
    const genderCode = parseInt(trimmed.charAt(16), 10);

    return {
      birthDate: `${year}-${month}-${day}`,
      gender: genderCode % 2 === 0 ? Gender.Female : Gender.Male,
      success: true,
    };
  }

  // 15位身份证（旧版）
  if (trimmed.length === 15) {
    const year = `19${trimmed.substring(6, 8)}`;
    const month = trimmed.substring(8, 10);
    const day = trimmed.substring(10, 12);
    const genderCode = parseInt(trimmed.charAt(14), 10);

    return {
      birthDate: `${year}-${month}-${day}`,
      gender: genderCode % 2 === 0 ? Gender.Female : Gender.Male,
      success: true,
    };
  }

  return { success: false };
}

/**
 * 从香港身份证号码中提取性别（无法提取出生日期）
 *
 * 香港身份证格式：X123456(A) 或 XX123456(A)
 * 注意：香港身份证号码不包含出生日期信息
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function parseHongKongIdCard(idNumber: string): IdCardParseResult {
  // 香港身份证无法提取出生日期，只返回解析失败
  return { success: false };
}

/**
 * 从台湾身份证号码中提取性别
 *
 * 台湾身份证格式：1个字母 + 9个数字
 * 第2位数字：1为男性，2为女性
 */
function parseTaiwanIdCard(idNumber: string): IdCardParseResult {
  const trimmed = idNumber.trim().toUpperCase();

  if (trimmed.length === 10 && /^[A-Z]\d{9}$/.test(trimmed)) {
    const genderCode = trimmed.charAt(1);

    if (genderCode === "1") {
      return { gender: Gender.Male, success: true };
    } else if (genderCode === "2") {
      return { gender: Gender.Female, success: true };
    }
  }

  return { success: false };
}

/**
 * 解析身份证号码，提取出生日期和性别
 *
 * @param idNumber 身份证号码
 * @param idType 证件类型
 * @returns 解析结果，包含出生日期和性别
 */
export function parseIdCard(idNumber: string | undefined, idType: IdType | undefined): IdCardParseResult {
  if (!idNumber || !idType) {
    return { success: false };
  }

  const trimmed = idNumber.trim();
  if (!trimmed) {
    return { success: false };
  }

  // 根据证件类型选择不同的解析方法
  // 支持多种 IdType 枚举值格式
  switch (idType) {
    case "CHINA_ID_CARD":
    case "CHINA_ID": // PersonForm 中使用的值
      return parseChinaIdCard(trimmed);

    case "HONG_KONG_ID_CARD":
    case "HONGKONG_ID": // PersonForm 中使用的值
      return parseHongKongIdCard(trimmed);

    case "TAIWAN_ID_CARD":
    case "TAIWAN_ID": // PersonForm 中使用的值
      return parseTaiwanIdCard(trimmed);

    // 护照、其他证件类型无法提取出生日期和性别
    case "PASSPORT":
    case "MACAO_ID":
    case "HK_MACAU_PASS":
    case "BIRTH_CERTIFICATE":
    case "OTHER":
    case "UNKNOWN":
    default:
      return { success: false };
  }
}

/**
 * 根据性别获取对应的称谓
 *
 * @param gender 性别
 * @returns 称谓（与性别值相同）
 */
export function getSalutationFromGender(gender: Gender | undefined): Gender | undefined {
  // 在当前系统中，称谓(salutation)和性别(gender)使用相同的枚举值
  return gender;
}
